import { cn } from '@/src/core/utils/cn'
import { forwardRef } from 'react'

export interface ButtonProperties extends React.ButtonHTMLAttributes<HTMLButtonElement> {
	variant?: 'primary' | 'secondary' | 'ghost' | 'preset' | 'increment'
	size?: 'sm' | 'md' | 'lg'
	loading?: boolean
	children: React.ReactNode
}

const buttonVariants = {
	variant: {
		primary: 'bg-[#66d697] text-black hover:bg-[#5bc285] focus:ring-[#66d697]',
		secondary: 'bg-white/5 text-white/70 hover:bg-white/10 hover:text-white focus:ring-[#66d697]',
		ghost: 'bg-transparent text-white/50 hover:bg-white/5 hover:text-white focus:ring-[#66d697]',
		preset: 'bg-white/5 text-white/70 hover:bg-white/10 hover:text-white focus:ring-[#66d697]',
		increment:
			'bg-white/5 text-white/50 hover:bg-white/10 hover:text-white focus:ring-[#66d697] border-white/10',
	},
	size: {
		sm: 'px-3 py-1.5 text-sm',
		md: 'px-4 py-2 text-sm',
		lg: 'px-6 py-3 text-base',
	},
}

export const Button = forwardRef<HTMLButtonElement, ButtonProperties>(
	(
		{ className, variant = 'primary', size = 'md', loading = false, disabled, children, ...properties },
		reference
	) => {
		return (
			<button
				type="button"
				className={cn(
					'inline-flex items-center justify-center rounded-md font-medium transition-colors',
					'focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-black',
					'disabled:cursor-not-allowed disabled:opacity-50',
					buttonVariants.variant[variant],
					buttonVariants.size[size],
					className
				)}
				disabled={disabled || loading}
				ref={reference}
				{...properties}
			>
				{loading ? (
					<>
						<svg
							className="mr-2 size-4 animate-spin"
							xmlns="http://www.w3.org/2000/svg"
							fill="none"
							viewBox="0 0 24 24"
						>
							<circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
							<path
								className="opacity-75"
								fill="currentColor"
								d="m4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
							/>
						</svg>
						{children}
					</>
				) : (
					children
				)}
			</button>
		)
	}
)

Button.displayName = 'Button'
